import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const PILLARS = [
  {
    n: "01",
    title: "We've run what we sell",
    body: "Our team has commissioned and operated commercial pyrolysis plants, including on difficult feedstock like cashew shell cake. The yields and uptime we quote come from the plant floor, not a brochure.",
  },
  {
    n: "02",
    title: "Carbon-ready from the reactor up",
    body: "Our plants feed live PLC/SCADA data straight into Convergent, our MRV platform. Carbon tracking starts on day one instead of being fitted later.",
  },
  {
    n: "03",
    title: "Every output earns",
    body: "Biochar is only part of the story. We engineer bio-oil, syngas and heat into revenue too, so the plant pays back faster.",
  },
  {
    n: "04",
    title: "The right route, not our route",
    body: "We work across pyrolysis, torrefaction, gasification and CBG, so we recommend what your biomass and market actually need.",
  },
  {
    n: "05",
    title: "We fix what really stops plants",
    body: "Most plants fail on feeding, moisture and handling, not the reactor. Drying, preprocessing and material handling are engineered in from the start.",
  },
  {
    n: "06",
    title: "Global standards, Indian cost base",
    body: "Designed to EBC and CPCB norms and adaptable to EU and other standards, at a cost that works for India, Southeast Asia and Africa.",
  },
];

const PageHeader = ({ eyebrow, title, blurb }) => (
  <section className="bg-paper text-char-900 pt-40 pb-20 lg:pb-28 border-b border-char-900/10">
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
      <h1 className="font-display mt-6 max-w-[22ch] text-char-900">
        {title}
      </h1>
      {blurb && (
        <p className="mt-8 max-w-[62ch] text-lg md:text-xl text-char-700 leading-relaxed">
          {blurb}
        </p>
      )}
    </div>
  </section>
);

const About = () => {
  usePageMeta(
    "About Tanren | Biomass Engineering Team",
    "Tanren was founded by an operating team with years of hands-on experience building and running commercial biomass plants."
  );
  return (
  <div data-testid="page-about">
    <PageHeader
      eyebrow="About Tanren"
      title="Built to close the gap between pilot decks and operating plants."
    />

    <section className="bg-paper py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14">
        <ScrollReveal className="md:col-span-5">
          <div className="mono-label">Origin</div>
          <h2 className="font-display mt-4 text-char-900">
            Founded by operators, not first-time builders.
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={100} className="md:col-span-7 space-y-6 text-lg text-char-700 leading-relaxed">
          <p>
            Tanren was founded by a team with direct, hands-on experience taking biochar and
            bioenergy ventures from lab reactor to commercially operating plant, and from tonnes
            of biomass to verified, revenue-generating carbon credits.
          </p>
          <p>
            We built Tanren because that round-trip is where most projects break. Feedstock studies
            live in one silo. Process engineering in another. Fabrication, MRV, and end-use markets
            each in yet another. Nobody owns the seams.
          </p>
          <p className="text-char-900">
            Tanren is the single partner that owns every seam — from "we have biomass waste" to
            "we have a commissioned, revenue-producing facility with a
            market for the output."
          </p>
        </ScrollReveal>
      </div>
    </section>

    <section className="bg-char-900 text-paper py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14">
        <ScrollReveal className="md:col-span-5">
          <div className="mono-label">Mission</div>
          <h2 className="font-display mt-4">
            Make bankable biomass plants the default, not the exception.
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={100} className="md:col-span-7">
          <p className="text-lg text-char-600 leading-relaxed">
            Global agriculture and industry produce hundreds of millions of tonnes of biomass
            residue every year. Almost all of it is burnt, dumped or under-utilized. Tanren exists
            to turn that residue into biochar, bio-coal, CBG, energy and permanent carbon storage,
            and into a revenue line — with engineering rigor and carbon-market discipline, at scale.
          </p>
        </ScrollReveal>
      </div>
    </section>

    <section className="bg-paper py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="mono-label">Why Tanren</div>
            <h2 className="font-display mt-4 text-char-900">
              What makes us different.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-char-700 text-lg leading-relaxed">
              Plenty of firms advise, sell machines or calculate carbon. Very few have done all of
              it on a working plant. Here is what that gives you.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
          {PILLARS.map((p, i) => (
            <ScrollReveal key={p.n} delay={i * 80}>
              <div
                data-testid={`about-pillar-${p.n}`}
                className="border border-char-900/10 bg-paper p-8 lg:p-10 h-full hover:border-char-900/25 transition-colors"
              >
                <div className="numeral text-3xl">{p.n}</div>
                <h3 className="mt-6 font-display text-char-900">
                  {p.title}
                </h3>
                <p className="mt-4 text-char-700 text-base leading-relaxed">{p.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap gap-4">
          <Link to="/advisory" className="btn-ember" data-testid="about-cta-services">
            See our capabilities
            <ArrowUpRight size={18} />
          </Link>
          <Link to="/contact" className="btn-ghost-light" data-testid="about-cta-contact">
            Talk to the team
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  </div>
  );
};

export default About;
